import JSZip from 'jszip'
import type { Message, Artifact, Participant, Reflection } from '../types'

export async function downloadSessionBundle(
  sessionName: string,
  messages: Message[],
  artifacts: Artifact[],
  participants: Participant[],
  reflections: Reflection[],
): Promise<void> {
  const zip = new JSZip()
  const bundleName = sessionName.replace(/\s+/g, '-').toLowerCase()

  // Separate agent-created files from user-uploaded files
  const agentFiles = artifacts.filter(a => a.uploadedBy === 'Scout' || a.uploadedBy === 'Archivist' || a.uploadedBy === 'Builder')
  const userFiles = artifacts.filter(a => a.uploadedBy !== 'Scout' && a.uploadedBy !== 'Archivist' && a.uploadedBy !== 'Builder')

  // Add agent-created deliverables as top-level files
  for (const a of agentFiles) {
    zip.file(a.fileName, a.content)
  }

  // Add user-uploaded docs in a subfolder
  if (userFiles.length > 0) {
    const uploads = zip.folder('uploaded-documents')!
    for (const a of userFiles) {
      uploads.file(a.fileName, a.content)
    }
  }

  // Build the session summary
  let summary = ''
  summary += `# ${sessionName}: Workshop Bundle\n\n`

  summary += `## Team\n\n`
  for (const p of participants) {
    summary += `- ${p.displayName}\n`
  }
  summary += '\n'

  // Deliverables index
  if (agentFiles.length > 0) {
    summary += `## Deliverables\n\n`
    for (const a of agentFiles) {
      summary += `- **${a.fileName}**\n`
    }
    summary += '\n'
  }

  // Reflection rounds
  if (reflections.length > 0) {
    summary += `## Reflection Rounds\n\n`
    for (const r of reflections) {
      summary += `### Stage ${r.stage}\n\n`
      summary += `**Prompt:** ${r.prompt}\n\n`
      for (const [pid, resp] of Object.entries(r.responses)) {
        const name = participants.find(p => p.id === pid)?.displayName || pid
        summary += `- **${name}:** ${resp.content}\n`
      }
      summary += '\n'
    }
  }

  // Full conversation log
  summary += `---\n\n## Full Conversation Log\n\n`
  for (const m of messages) {
    const sender = m.participantId === 'agent'
      ? (m.agentName || 'AI')
      : m.participantId === 'system'
        ? 'System'
        : (participants.find(p => p.id === m.participantId)?.displayName || 'Unknown')
    summary += `**${sender}:** ${m.content}\n\n`
  }

  zip.file('session-summary.md', summary)

  const blob = await zip.generateAsync({ type: 'blob' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${bundleName}-bundle.zip`
  a.click()
  URL.revokeObjectURL(url)
}
